import React, { useEffect, useState } from 'react';
import { Truck, Undo2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog';
import { Button } from './primitives';
import { SHIPMENT_DIRECTION } from '../shared/orderShipments';

/*
 * The question a status change asks when it starts a trip.
 *
 * "Handed to shipping company" means a parcel just left under a number from
 * Tamex, and "Returned" means one is on its way back under another: the
 * moment the status is set is the moment the number is in hand. Shopify's
 * "Mark as fulfilled" asks for the tracking number right there for the same
 * reason — asked later, it is forgotten, and the customer's order page says
 * "shipped" with nothing to follow. So every status menu routes these two
 * moves here, unless the order's newest trip already goes that way (a number
 * keyed in ahead of the move, or a correction back and forth).
 *
 * A trip out needs its number. A trip back may be recorded without one: a
 * parcel the courier could not deliver comes back under the number it left
 * with. The hint under the box says so; nothing else is optional.
 */

const INPUT_CLASS =
  'w-full h-9 rounded-lg border border-[#D6DFE2] px-2.5 text-[13px] text-nysmah-dark bg-white focus:outline-none focus:border-nysmah-accent focus:ring-1 focus:ring-nysmah-accent';

/** Title, body and button for the two moves, in the words the status menus use. */
export const trackingPromptCopy = ({ direction, orderNumber, statusLabel, courierLabel }) =>
  direction === SHIPMENT_DIRECTION.RETURN
    ? {
        required: false,
        title: `Mark order #${orderNumber} as returned?`,
        description: `Its items go back into stock. From there it can be sent out again from the status menu, or cancelled — the cancel is what releases the payment and any loyalty points. Add the return tracking number from ${courierLabel} if there is one.`,
        confirmLabel: 'Mark as returned',
      }
    : {
        required: true,
        title: `Hand order #${orderNumber} to ${courierLabel}?`,
        description: `Paste the tracking number ${courierLabel} gave you. The customer gets it by email and can follow the parcel from their order page.`,
        confirmLabel: `Mark ${statusLabel}`,
      };

const TrackingPromptDialog = ({
  open,
  onOpenChange,
  direction,
  courierLabel,
  title,
  description,
  confirmLabel,
  required = true,
  isPending = false,
  onConfirm,
}) => {
  const [draft, setDraft] = useState('');
  const isReturn = direction === SHIPMENT_DIRECTION.RETURN;
  const Icon = isReturn ? Undo2 : Truck;

  // A fresh box every time it opens: the last order's number must never be
  // sitting there waiting for a careless Enter.
  useEffect(() => {
    if (open) setDraft('');
  }, [open]);

  const value = draft.trim();
  const canConfirm = !isPending && (Boolean(value) || !required);
  const submit = () => {
    if (!canConfirm) return;
    onConfirm(value || null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isPending) onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-semibold text-nysmah-dark">
            <span
              className={`flex items-center justify-center w-7 h-7 rounded-lg shrink-0 ${
                isReturn ? 'bg-[#FDF4E3] text-[#8A6508]' : 'bg-[#E8F3EE] text-[#256B4C]'
              }`}
              aria-hidden="true"
            >
              <Icon className="w-4 h-4" />
            </span>
            {title}
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-nysmah-muted">
            {description}
          </DialogDescription>
        </DialogHeader>

        <div>
          <label
            htmlFor="status-tracking-number"
            className="block text-[11px] font-semibold text-[#4A5B66] mb-1"
          >
            {isReturn ? `${courierLabel} return tracking number` : `${courierLabel} tracking number`}
          </label>
          <input
            id="status-tracking-number"
            type="text"
            value={draft}
            maxLength={64}
            autoComplete="off"
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit();
            }}
            placeholder={`Paste the number from ${courierLabel}`}
            className={INPUT_CLASS}
          />
          {!required && (
            <p className="m-0 mt-1.5 text-[11.5px] text-nysmah-muted">
              Leave it empty if the parcel came back under the number it left with.
            </p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
            Keep as is
          </Button>
          <Button
            variant={isReturn ? 'danger' : 'primary'}
            onClick={submit}
            disabled={!canConfirm}
          >
            {isPending ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TrackingPromptDialog;
